import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { ACCOUNT_FEATURE, selectUserName, selectUserRolePermissions } from '@socialive/common';
import { useFeature } from '@socialive/features';
import { useSelector } from 'react-redux';
import CreateBroadcast from '../CreateBroadcast';
import DropdownMenu from './DropdownMenu';
import HeaderLogo from './HeaderLogo';
import NavMenu from './NavMenu';

import debounce from '../../helpers/debounce';
import getInitials from '../../helpers/getInitials';
import '../../style/header.css';
import CreateRecording from '../CreateRecording';
import UploadMediaButton from '../UploadMediaButton';

const OVERFLOW_HEIGHT = 19;

export default function GlobalHeader({ loadBroadcasts, reAuth, setChargebee, switchAccount, trackEvent }) {
  // logic to detect name overflow
  const nameRef = useRef(null);
  const [overflow, setOverflow] = useState(false);

  const userName = useSelector(selectUserName);
  const { canNavigateInPortal, canViewOwnRecordings } = useSelector(selectUserRolePermissions);
  const [, { enabled: isRolesFeatureEnabled }] = useFeature(ACCOUNT_FEATURE.USER_ROLES);
  const [, { enabled: isSoloRecorderEnabled }] = useFeature(ACCOUNT_FEATURE.SOLO_RECORDER);

  const shouldShowInitialsIcon =
    canNavigateInPortal || (isRolesFeatureEnabled && !canNavigateInPortal && canViewOwnRecordings);

  const [modal, setModal] = useState({
    main: false,
    appMenu: false,
  });

  useEffect(() => {
    setChargebee();

    setOverflow(nameRef?.current?.offsetHeight > OVERFLOW_HEIGHT);

    function handleResize() {
      setOverflow(false);
      if (nameRef?.current?.offsetHeight > OVERFLOW_HEIGHT) {
        setOverflow(true);
      }
    }

    const debouncedResize = debounce(handleResize, 200);

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [setChargebee]);

  return (
    <header className="global-header-container">
      <HeaderLogo />
      <NavMenu />
      <div className="header-account-avatar">
        {isSoloRecorderEnabled && (
          <CreateRecording isHeader loadBroadcasts={loadBroadcasts} reAuth={reAuth} trackEvent={trackEvent} />
        )}
        <UploadMediaButton />
        <CreateBroadcast isHeader />
        <button
          type="button"
          className="header-account-avatar-button"
          onClick={() => setModal({ ...modal, main: true })}
        >
          <span
            className={`header-account-name ${shouldShowInitialsIcon || overflow ? 'initials-icon' : ''}`}
            ref={nameRef}
          >
            {shouldShowInitialsIcon || overflow ? getInitials(userName) : userName}
          </span>
          <i className="icon-triangle-down" />
        </button>
        <DropdownMenu
          handleModal={(main, appMenu) => setModal({ main, appMenu })}
          modal={modal}
          switchAccount={switchAccount}
          trackEvent={trackEvent}
        />
      </div>
    </header>
  );
}

GlobalHeader.propTypes = {
  loadBroadcasts: PropTypes.func.isRequired,
  reAuth: PropTypes.func.isRequired,
  setChargebee: PropTypes.func.isRequired,
  switchAccount: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};
